import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Button, Input, List, Popconfirm, Tag, Tooltip, Typography } from 'antd'
import { useMemo, useState } from 'react'
import { useDebouncedState } from '../../../../hooks/useDebouncedState'
import VSpace from '../../../common/VSpace'
import {
  CASES_PAGE_SIZE,
  downloadCaseJson,
  EvalCase,
  filterCasesByName,
} from './casesAdminModel'
import { useCasesListQuery, useDeleteCaseMutation } from './casesAdminQueries'
import CaseEditorModal from './CaseEditorModal'

const CasesExplorer = () => {
  // The list endpoint returns every case, so search and paging are client-side.
  const {
    state: search,
    debouncedState: debouncedSearch,
    setState: setSearch,
  } = useDebouncedState('', 200)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<EvalCase | null>(null)

  const { data, isLoading } = useCasesListQuery()
  const deleteMutation = useDeleteCaseMutation()

  const cases = useMemo(
    () => filterCasesByName(data?.cases ?? [], debouncedSearch),
    [data, debouncedSearch],
  )

  const renderItem = (evalCase: EvalCase) => (
    <List.Item
      actions={[
        <Tooltip key="delete" title="Delete case">
          <Popconfirm
            title="Delete this case?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMutation.mutate(evalCase.case_id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Tooltip>,
        <Tooltip key="edit" title="Edit case">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => setEditing(evalCase)}
          />
        </Tooltip>,
        <Tooltip key="download" title="Download JSON">
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => downloadCaseJson(evalCase)}
          />
        </Tooltip>,
      ]}
    >
      <List.Item.Meta
        title={<Typography.Text strong>{evalCase.name}</Typography.Text>}
        description={
          <span>
            <Tag>{evalCase.scenario}</Tag>
            {evalCase.body.judge ? (
              <Tag color="blue">judge: {evalCase.body.judge.rubric}</Tag>
            ) : (
              <Tag>assertions only</Tag>
            )}
            {evalCase.body.golden_prompt_id && (
              <Tag color="orange">from golden prompt</Tag>
            )}
            {evalCase.tags.map((t) => (
              <Tag key={t.tag_id} color="geekblue">
                {t.name}
              </Tag>
            ))}
          </span>
        }
      />
    </List.Item>
  )

  return (
    <VSpace>
      <Typography.Title level={4} style={{ marginBottom: 0 }}>
        Cases
      </Typography.Title>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Input.Search
          placeholder="Search by name"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 480 }}
        />
        <Tooltip title="New case">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreating(true)}
          />
        </Tooltip>
      </div>
      <List
        bordered
        loading={isLoading}
        dataSource={cases}
        locale={{ emptyText: 'No cases' }}
        renderItem={renderItem}
        pagination={
          cases.length > CASES_PAGE_SIZE
            ? { pageSize: CASES_PAGE_SIZE, showSizeChanger: false, align: 'end' }
            : false
        }
      />
      {creating && <CaseEditorModal open onClose={() => setCreating(false)} />}
      {editing && (
        <CaseEditorModal
          open
          evalCase={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </VSpace>
  )
}

export default CasesExplorer
